import { addMinutes, differenceInMilliseconds, startOfMinute } from 'date-fns';

import { PriceDisplay } from '~/types/axis.types';
import { BaseChartAPIProps } from '~/types/create.types';
import { ResampleIntervals } from '~/types/resample.types';
import { getPercentage } from '~/utils/y-scale/y-scale';

import { drawCrosshair } from './crosshair';

export const getNextBoundaryUTC = (
  date: Date,
  interval: ResampleIntervals
): Date => {
  if (interval !== '1m') {
    return date;
  }

  return startOfMinute(addMinutes(date, 1));
};

export const drawLiveCrosshair = (base: BaseChartAPIProps) => {
  const primaryPane = base.panes.find(p => p.id === base.primaryPaneId);

  if (!primaryPane) {
    return;
  }

  const primaryAsset = primaryPane.chartAssetData[0];

  if (!primaryAsset || primaryAsset.ts.length === 0) {
    return;
  }

  const lastIdx = primaryAsset.ts.length - 1;
  const lastBar = primaryAsset.ts[lastIdx];
  const isMerged = primaryPane.yAxisType === 'merged';
  const axisIndex = primaryPane.y.findIndex(
    yAxis => yAxis.entityDefaultSnake === primaryAsset.entity.default_snake
  );

  if (axisIndex === -1) {
    return;
  }

  const yAxis = primaryPane.y[axisIndex];
  const priceDisplay: PriceDisplay = isMerged ? 'percentage' : 'price';
  const baseValue =
    primaryPane.domainTimeSeries[axisIndex]?.start.close ??
    primaryAsset.ts[0].close;
  const value =
    priceDisplay === 'price'
      ? lastBar.close
      : getPercentage(lastBar.close, baseValue);
  const yCoord = yAxis.yScale(value);
  const previousClose =
    lastIdx > 0 ? primaryAsset.ts[lastIdx - 1].close : lastBar.close;
  const isBullish = lastBar.close >= previousClose;
  const lineColor = isBullish
    ? base.options.colors.bars.up.value
    : base.options.colors.bars.down.value;
  const backgroundColor = isBullish
    ? base.options.colors.threshold.bullish.value
    : base.options.colors.threshold.bearish.value;
  const circleColor = lineColor;

  let subtitle;

  if (base.resampleInterval === '1m') {
    const nextBoundaryFromBar = getNextBoundaryUTC(
      new Date(lastBar.time),
      base.resampleInterval
    );
    const remainingMs = Math.max(
      0,
      differenceInMilliseconds(nextBoundaryFromBar, new Date())
    );
    const secs = Math.max(0, Math.ceil(remainingMs / 1000));
    subtitle = `0:${secs.toString().padStart(2, '0')}`;
  }

  drawCrosshair({
    ...base,
    x1: base.x.xScale(lastIdx),
    drawVertical: false,
    date: new Date(lastBar.time),
    subtitle,
    hoveredPoints: [
      {
        value,
        circleColor,
        lineColor,
        backgroundColor,
        y: yAxis,
        yCoord,
        priceDisplay,
        shouldDrawCircle: true,
        yAxisSize: base.yAxisSizes[axisIndex],
      },
    ],
  });
};
